import { useEffect, useState, memo, useRef } from "react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useTranslation } from "next-i18next";
import { MdOutlineFileUpload } from "react-icons/md";
import { useRouter } from "next/router";
import PhoneInput from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";
import "react-phone-number-input/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";

import axiosInstance from "../utils/axiosInstance";
import { getRecaptchaToken } from "../utils/getcapthcatoken";
import { postApplyForm } from "./http/commonApi";

const initialObject = {
  name: "",
  previousCompanyWorked: "",
  phone: "",
  email: "",
  cv: "",
  message: "",
};

const phoneLabels = {
  ...en,
  country: "Country",
  phone: "Phone number",
};

const CareersForm = memo(({ careerId, className = "" }) => {
  const { t } = useTranslation("careers");
  const router = useRouter();
  const lang = router.locale || "en";

  const hiddenFileInput = useRef(null);

  const [formValues, setFormValues] = useState(initialObject);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadFile, setUploadFile] = useState([]);

  const [formToken, setFormToken] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [websiteTrap, setWebsiteTrap] = useState("");

  useEffect(() => {
    const loadToken = async () => {
      try {
        const res = await axiosInstance.get(`/form-token?lang=${lang}`);
        setFormToken(res?.data?.formToken || "");
      } catch (err) {
        console.error("Failed to load form token", err);
        setFormToken("");
      }
    };

    loadToken();
  }, [lang]);

  const handleClick = () => {
    hiddenFileInput.current?.click();
  };

  const handleFileChange = (event) => {
    const maxFileSize = 5 * 1024 * 1024;
    const file = event.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    // ❌ INVALID FILE TYPE
    if (!allowedTypes.includes(file.type)) {
      setUploadFile([]);

      // 👉 ADD THIS LINE
      if (hiddenFileInput.current) hiddenFileInput.current.value = "";

      setErrors((prev) => ({
        ...prev,
        cv: "Only PDF, DOC, or DOCX files are allowed.",
      }));
      return;
    }

    // ❌ FILE TOO LARGE
    if (file.size > maxFileSize) {
      setUploadFile([]);

      // 👉 ADD THIS LINE
      if (hiddenFileInput.current) hiddenFileInput.current.value = "";

      setErrors((prev) => ({
        ...prev,
        cv: "File too large. Max 5MB allowed.",
      }));
      return;
    }

    // ✅ VALID FILE
    setUploadFile([{ image: file }]);
    setErrors((prev) => ({ ...prev, cv: "" }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let nextValue = value;

    if (name === "name") {
      nextValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhoneChange = (value) => {
    setFormValues((prev) => ({ ...prev, phone: value || "" }));
    setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const getValidatedMobilePhone = (phone) => {
    if (!phone) return { ok: false, reason: "empty", normalized: "" };

    try {
      const parsed = parsePhoneNumberFromString(phone);

      if (!parsed || !parsed.isValid()) {
        return { ok: false, reason: "invalid", normalized: "" };
      }

      const type = parsed.getType();

      if (type !== "MOBILE" && type !== "FIXED_LINE_OR_MOBILE") {
        return {
          ok: false,
          reason: "non_mobile",
          normalized: parsed.number || "",
          type,
        };
      }

      return {
        ok: true,
        reason: "",
        normalized: parsed.number,
        type,
      };
    } catch {
      return { ok: false, reason: "invalid", normalized: "" };
    }
  };

  const getNormalizedPhone = () => {
    const result = getValidatedMobilePhone(formValues.phone);
    return result.ok ? result.normalized : "";
  };

  const validateForm = () => {
    const nextErrors = {};
    const { name, previousCompanyWorked, email, phone, message } = formValues;

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const nameRegex = /^[A-Za-z\s]+$/;

    if (!name.trim()) {
      nextErrors.name = "Please Enter Full Name.";
    } else if (!nameRegex.test(name.trim())) {
      nextErrors.name = "Full Name must contain letters only.";
    }

    if (!previousCompanyWorked.trim()) {
      nextErrors.previousCompanyWorked = "Please Enter Previous Company Name.";
    }

    const phoneCheck = getValidatedMobilePhone(phone);

    if (!phone) {
      nextErrors.phone = "Please Enter Phone Number.";
    } else if (!phoneCheck.ok) {
      if (phoneCheck.reason === "invalid") {
        nextErrors.phone = "Please enter a valid phone number.";
      } else if (phoneCheck.reason === "non_mobile") {
        nextErrors.phone =
          "Only mobile numbers are allowed (no landline, toll-free, or VoIP).";
      }
    }

    if (!email.trim()) {
      nextErrors.email = "Please Enter Email.";
    } else if (!emailRegex.test(email.trim())) {
      nextErrors.email = "Invalid email address.";
    }

    if (!uploadFile || uploadFile.length === 0) {
      nextErrors.cv = "Please Upload CV.";
    }

    if (!message.trim()) {
      nextErrors.message = "Please Enter Message.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const PostApplyFormData = async (imagesFormData) => {
    try {
      const headers = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      const response = await postApplyForm(
        careerId,
        lang,
        imagesFormData,
        headers,
      );

      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 202
      ) {
        toast.success(
          response.data.message ||
            "We have received your application. Our team will get in touch with you shortly.",
        );

        setFormValues({ ...initialObject });
        setUploadFile([]);
        setErrors({});

        if (hiddenFileInput.current) {
          hiddenFileInput.current.value = "";
        }

        try {
          const res = await axiosInstance.get(`/form-token?lang=${lang}`);
          setFormToken(res?.data?.formToken || "");
        } catch {
          setFormToken("");
        }
      }
    } catch (error) {
      console.error("Error posting Data:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!formToken) {
      toast.error("Please wait a moment and try again.");
      return;
    }

    const isValid = validateForm();
    if (!isValid) return;

    setLoading(true);

    try {
      const recaptchaToken = await getRecaptchaToken("career_apply_submit");
      const timeOnPageMs = Date.now() - startedAt;
      const phoneCheck = getValidatedMobilePhone(formValues.phone);

      const imagesFormData = new FormData();

      uploadFile.forEach((fileItem) => {
        if (fileItem?.image) {
          imagesFormData.append("cv", fileItem.image);
        }
      });

      imagesFormData.append("name", formValues.name.trim());
      imagesFormData.append(
        "previousCompanyWorked",
        formValues.previousCompanyWorked.trim(),
      );
      imagesFormData.append("phone", phoneCheck.normalized);
      imagesFormData.append("email", formValues.email.trim().toLowerCase());
      imagesFormData.append("message", formValues.message.trim());

      imagesFormData.append("formToken", formToken);
      imagesFormData.append("timeOnPageMs", String(timeOnPageMs));
      imagesFormData.append("website", websiteTrap);
      imagesFormData.append("recaptchaToken", recaptchaToken);

      await PostApplyFormData(imagesFormData);
    } catch (error) {
      console.error("Career apply protection error:", error);
      toast.error("Something went wrong. Please try again later.");
      setLoading(false);
    }
  };

  const normalizedPreview = getNormalizedPhone();

  return (
    <section
      className={`w-full bg-text-primary overflow-hidden flex flex-row items-start justify-start py-[40px] lg:py-[60px] px-6 sm:px-6 lg:px-20 box-border max-w-full text-45xl text-background-color-primary ${className}`}
    >
      <style jsx global>{`
        .custom-phone-input {
          --PhoneInputCountryFlag-height: 18px;
          --PhoneInput-color--focus: transparent;
        }

        .custom-phone-input .PhoneInput {
          display: flex;
          align-items: center;
          width: 100%;
          gap: 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.24);
          padding-bottom: 8px;
        }

        .custom-phone-input .PhoneInputCountry {
          margin-right: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .custom-phone-input .PhoneInputCountrySelect {
          background: transparent;
          color: inherit;
          border: 0;
          outline: none;
          font-size: 16px;
          cursor: pointer;
          width: 100%;
        }

        .custom-phone-input .PhoneInputCountrySelect option {
          color: #111827;
        }

        .custom-phone-input .PhoneInputCountryIcon {
          box-shadow: none;
          background: transparent;
        }

        .custom-phone-input .PhoneInputCountryIconImg {
          display: block;
        }

        .custom-phone-input .PhoneInputInput {
          flex: 1;
          width: 100%;
          min-width: 0;
          background: transparent;
          color: inherit;
          border: 0;
          outline: none;
          font-size: 16px;
          padding: 0;
        }

        .custom-phone-input .PhoneInputInput::placeholder {
          color: white;
          opacity: 1;
        }

        .custom-phone-input .PhoneInputCountrySelectArrow {
          opacity: 0.8;
        }
      `}</style>

      <div className="w-full box-border flex-1 flex flex-col lg:flex-row items-start justify-start gap-[60px] max-w-[1600px] mx-auto">
        <h2 className="m-0 w-full lg:w-1/3 relative text-[28px] sm:text-[32px] lg:text-[50px] leading-[120%] font-medium font-[inherit] inline-block shrink-0 max-w-full">
          {t("careers.apply.title")}
        </h2>

        <form
          id="career_form"
          className="career_form flex-1 flex flex-col items-start justify-start gap-6 w-full box-border text-base"
          onSubmit={handleSubmit}
        >
          <div className="self-stretch flex flex-col items-start justify-start gap-8 max-w-full w-full">
            <div className="self-stretch flex flex-col lg:flex-row items-start justify-start gap-4 max-w-full">
              <div className="w-full flex-1 flex flex-col gap-2">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formValues.name}
                  onChange={handleInputChange}
                  className="text-[16px] border-b border-gray-300 bg-transparent text-background-color-primary outline-none pb-2 placeholder:text-white"
                  placeholder={t("careers.apply.form.name")}
                  inputMode="text"
                  autoComplete="name"
                />
                {errors?.name && (
                  <p className="m-0 text-[14px]" style={{ color: "red" }}>
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="w-full flex-1 flex flex-col gap-2">
                <input
                  type="text"
                  id="previousCompanyWorked"
                  name="previousCompanyWorked"
                  value={formValues.previousCompanyWorked}
                  onChange={handleInputChange}
                  className="text-[16px] border-b border-gray-300 bg-transparent text-background-color-primary outline-none pb-2 placeholder:text-white"
                  placeholder={t("careers.apply.form.company")}
                  autoComplete="organization"
                />
                {errors?.previousCompanyWorked && (
                  <p className="m-0 text-[14px]" style={{ color: "red" }}>
                    {errors.previousCompanyWorked}
                  </p>
                )}
              </div>
            </div>

            <div className="self-stretch flex flex-col lg:flex-row items-start justify-start gap-4 max-w-full">
              <div className="w-full flex-1 flex flex-col gap-2" dir="ltr">
                <div className="custom-phone-input">
                  <PhoneInput
                    id="phone"
                    name="phone"
                    defaultCountry="AE"
                    international
                    withCountryCallingCode
                    labels={phoneLabels}
                    value={formValues.phone}
                    onChange={handlePhoneChange}
                    className="w-full text-background-color-primary"
                  />
                </div>

                {errors?.phone ? (
                  <p className="m-0 text-[14px]" style={{ color: "red" }}>
                    {errors.phone}
                  </p>
                ) : (
                  <p className="m-0 text-[14px]" style={{ color: "orange" }}>
                    {normalizedPreview
                      ? `Will send as: ${normalizedPreview}`
                      : "Use international format +971xx, +91xx, etc."}
                  </p>
                )}
              </div>

              <div className="w-full flex-1 flex flex-col gap-2">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  className="text-[16px] border-b border-gray-300 bg-transparent text-background-color-primary outline-none pb-2 placeholder:text-white"
                  placeholder={t("careers.apply.form.email")}
                  autoComplete="email"
                />
                {errors?.email && (
                  <p className="m-0 text-[14px]" style={{ color: "red" }}>
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="w-full flex flex-col gap-2">
              <div className="relative cursor-pointer" onClick={handleClick}>
                <input
                  type="text"
                  id="cv"
                  name="cv"
                  className="text-[16px] cursor-pointer border-b border-gray-300 bg-transparent text-background-color-primary outline-none pb-2 w-full placeholder:text-white"
                  placeholder={t("careers.apply.form.upload")}
                  readOnly
                  value={
                    uploadFile?.length > 0
                      ? uploadFile?.[0]?.image?.name
                      : t("careers.apply.form.upload")
                  }
                />
                <MdOutlineFileUpload
                  className={`absolute top-0 ${
                    lang === "ar" ? "left-0" : "right-0"
                  }`}
                  fontSize="20px"
                />
              </div>

              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                ref={hiddenFileInput}
                onChange={handleFileChange}
                style={{ display: "none" }}
                name="cv"
              />

              {errors?.cv && (
                <p className="m-0 text-[14px]" style={{ color: "red" }}>
                  {errors.cv}
                </p>
              )}
            </div>

            <div className="w-full flex flex-col gap-2">
              <textarea
                id="message"
                name="message"
                value={formValues.message}
                onChange={handleInputChange}
                rows={4}
                className="text-[16px] border-b border-gray-300 bg-transparent text-background-color-primary outline-none pb-2 w-full resize-none placeholder:text-white"
                placeholder={t("careers.apply.form.message")}
              />
              {errors?.message && (
                <p className="m-0 text-[14px]" style={{ color: "red" }}>
                  {errors.message}
                </p>
              )}
            </div>
          </div>

          <input
            type="text"
            name="website"
            value={websiteTrap}
            onChange={(e) => setWebsiteTrap(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            style={{
              position: "absolute",
              left: "-10000px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          />

          <button
            type="submit"
            className="rounded-[50px] bg-crimson text-white text-[16px] font-medium py-3 px-6 cursor-pointer hover:bg-[#e72c46] transition duration-300 ease-in-out disabled:cursor-not-allowed disabled:opacity-70 career_form"
            disabled={loading || !formToken}
          >
            {loading ? "Sending..." : t("careers.apply.form.btnTxt")}
          </button>
        </form>
      </div>
    </section>
  );
});

CareersForm.displayName = "CareersForm";

CareersForm.propTypes = {
  careerId: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default CareersForm;
